import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ShoppingCart, Plus, Minus } from 'lucide-react';
import { menuCategories } from '../mock';

const Menu = () => {
  const [selectedCategory, setSelectedCategory] = useState('combos');
  const [cart, setCart] = useState([]);

  const addToCart = (item) => {
    const existingItem = cart.find(i => i.id === item.id);
    if (existingItem) {
      setCart(cart.map(i => 
        i.id === item.id ? { ...i, quantity: i.quantity + 1 } : i
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId) => {
    const existingItem = cart.find(i => i.id === itemId);
    if (existingItem && existingItem.quantity > 1) {
      setCart(cart.map(i => 
        i.id === itemId ? { ...i, quantity: i.quantity - 1 } : i
      ));
    } else {
      setCart(cart.filter(i => i.id !== itemId));
    }
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const currentCategory = menuCategories.find(cat => cat.id === selectedCategory);

  return (
    <section id="menu" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Nuestro <span className="text-[#7CB342]">Menú</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Descubre nuestras deliciosas opciones preparadas con ingredientes frescos y naturales
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {menuCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-[#7CB342] text-white shadow-lg scale-105'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="mr-2">{category.icon}</span>
              {category.name}
            </button>
          ))}
        </div>

        {/* Category Description */}
        {currentCategory?.description && (
          <div className="text-center mb-8">
            <p className="text-gray-500 italic">{currentCategory.description}</p>
          </div>
        )}

        {/* Menu Items Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {currentCategory?.items.map((item) => {
            const cartItem = cart.find(i => i.id === item.id);
            return (
              <Card
                key={item.id}
                className="overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-[#7CB342]/30"
              >
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-xl flex-1">{item.name}</CardTitle>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className="bg-[#FFB830] hover:bg-[#FFA500] text-white font-bold">
                        S/ {item.price.toFixed(2)}
                      </Badge>
                      {item.badge && (
                        <Badge variant="outline" className="border-[#7CB342] text-[#7CB342] text-xs">
                          {item.badge}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-gray-600">
                    {item.description}
                  </CardDescription>
                  {item.discount && (
                    <p className="text-sm text-green-600 font-semibold mt-2">💰 {item.discount}</p>
                  )}
                </CardHeader>
                <CardContent>
                  {cartItem ? (
                    <div className="flex items-center justify-between bg-[#7CB342]/10 rounded-lg p-3">
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="bg-white text-[#7CB342] rounded-full p-2 hover:bg-gray-100 transition-colors"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="font-bold text-lg">{cartItem.quantity}</span>
                      <button
                        onClick={() => addToCart(item)}
                        className="bg-[#7CB342] text-white rounded-full p-2 hover:bg-[#6FA03A] transition-colors"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => addToCart(item)}
                      className="w-full bg-[#7CB342] hover:bg-[#6FA03A] text-white font-semibold rounded-lg transition-all duration-300"
                    >
                      <ShoppingCart className="mr-2" size={18} />
                      Agregar al Carrito
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Cart Summary */}
        {cart.length > 0 && (
          <div className="fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl border-2 border-[#7CB342]/30 p-6 max-w-sm z-40">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">Tu Pedido</h3>
              <Badge className="bg-[#FFB830] hover:bg-[#FFA500] text-white">
                {getTotalItems()} items
              </Badge>
            </div>
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    {item.quantity}x {item.name}
                  </span>
                  <span className="font-semibold">S/ {(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold">Total:</span>
                <span className="text-2xl font-bold text-[#7CB342]">S/ {getTotalPrice().toFixed(2)}</span>
              </div>
            </div>
            <Button className="w-full bg-[#FFB830] hover:bg-[#FFA500] text-white font-bold py-3 rounded-lg">
              Realizar Pedido por WhatsApp
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Menu;
